/**
 * Screener → intake cross-write.
 *
 * The eligible-outcome path saves a typed snapshot of the screener's
 * answers to `localStorage` under a stable key. On first mount of the
 * intake, the App reads the snapshot, projects screener answers into
 * the catalog's `eligibility.*` / `sponsor.*` field IDs, and seeds
 * the form engine with them. After hydration the snapshot is left in
 * place (idempotent across reloads); a follow-up Phase 5 slice will
 * clear it after the intake's first persist.
 *
 * Why localStorage, not Firestore: the screener is anonymous in v1,
 * so we don't have a Firestore document yet. The user signs up
 * AFTER the eligible outcome — at that moment the intake has a real
 * `applicationId`, and this module bridges the two with a short
 * localStorage hop.
 *
 * Snapshot shape is versioned so a future schema change can detect
 * stale handoffs and drop them gracefully.
 */
import type { AnswerValue, FieldId } from '@inside-us-aos/domain';

import { type IScreenerAnswers } from './screenerRouter';

/** Single-tenant-per-browser key. The screener is anonymous; we
 *  don't multiplex by user. */
// Bumped from v1 → v2 in Slice 9.2 (2026-05-17) when the 12-question
// boolean-bar block collapsed into two multi-select bundles. Pre-v2
// handoffs reference dropped screener question ids that no longer
// exist; reading them would emit "field not found" diagnostics. The
// version bump invalidates pre-v2 entries so the user re-runs the
// (shorter) screener cleanly.
export const SCREENER_HANDOFF_STORAGE_KEY = 'inside-us-aos:screener-handoff:v2';

/**
 * Snapshot shape persisted on the eligible cross-write. The shape is
 * versioned so future migrations can detect stale handoffs.
 */
export interface IScreenerHandoffSnapshot {
  readonly version: 1;
  /** ISO-8601 timestamp of the cross-write. */
  readonly capturedAt: string;
  /** The full screener answer set (so intake can replay if needed). */
  readonly answers: IScreenerAnswers;
}

/**
 * Persist a screener-eligible snapshot for the intake to pick up.
 * Idempotent: overwrites prior snapshots so a re-take with new
 * answers doesn't mix old and new state.
 */
export function saveScreenerHandoff(
  answers: IScreenerAnswers,
  storage: Storage = defaultStorage(),
): void {
  const snapshot: IScreenerHandoffSnapshot = {
    version: 1,
    capturedAt: new Date().toISOString(),
    answers,
  };
  try {
    storage.setItem(SCREENER_HANDOFF_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage quota / private-window / disabled — non-fatal. The
    // intake will simply not pre-populate; the user re-answers.
  }
}

/**
 * Load a previously-persisted screener handoff. Returns `null` when
 * the slot is empty, the JSON is malformed, or the version doesn't
 * match the current shape.
 */
export function loadScreenerHandoff(
  storage: Storage = defaultStorage(),
): IScreenerHandoffSnapshot | null {
  let raw: string | null;
  try {
    raw = storage.getItem(SCREENER_HANDOFF_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as IScreenerHandoffSnapshot;
    if (parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Clear the handoff slot — typically after the intake's first persist. */
export function clearScreenerHandoff(
  storage: Storage = defaultStorage(),
): void {
  try {
    storage.removeItem(SCREENER_HANDOFF_STORAGE_KEY);
  } catch {
    // Same fallback rules as save — non-fatal.
  }
}

function defaultStorage(): Storage {
  if (
    typeof globalThis !== 'undefined' &&
    'localStorage' in globalThis &&
    (globalThis as { localStorage?: Storage }).localStorage
  ) {
    return (globalThis as unknown as { localStorage: Storage }).localStorage;
  }
  // Memory shim — used in SSR / tests where localStorage is missing.
  // The shim is local to the call and discarded after use.
  return memoryStorage();
}

function memoryStorage(): Storage {
  const store = new Map<string, string>();
  return {
    get length(): number {
      return store.size;
    },
    clear(): void {
      store.clear();
    },
    getItem(key: string): string | null {
      return store.has(key) ? (store.get(key) ?? null) : null;
    },
    key(index: number): string | null {
      return Array.from(store.keys())[index] ?? null;
    },
    removeItem(key: string): void {
      store.delete(key);
    },
    setItem(key: string, value: string): void {
      store.set(key, value);
    },
  };
}

/**
 * Project a screener answer set into a list of `(FieldId, AnswerValue)`
 * pairs the form engine can ingest. Only catalog-bound screener
 * answers are projected — screener-internal questions (with
 * `catalogFieldId === null`) are dropped on purpose, because there's
 * no catalog field to write into.
 *
 * The severe-harm bundle fans out into the four catalog booleans it
 * consolidates: every harm value the user picked sets the matching
 * boolean to `true`; the remaining booleans default to `false`. The
 * special "none" sentinel sets all four to `false`.
 *
 * The router's binary outcome routing is honored elsewhere; this
 * function exists strictly to seed the intake with the user's
 * already-given answers so the intake can show partial section
 * progress immediately.
 */
/**
 * Apply a list of projections to the engine,
 * skipping any projection that throws (e.g., projection targets a
 * field that turned repeatable in a later catalog rev — engine throws
 * "requires a groupInstanceId" — without this guard the throw
 * propagates to the React tree and crashes the page via the Sentry
 * ErrorBoundary).
 *
 * Skipped projections are reported via the supplied `onError`
 * callback so the failure stays observable in production monitoring;
 * the per-iteration try/catch is the difference between "one bad
 * projection logs an error" and "the whole sign-up page is replaced
 * by the error fallback UI."
 *
 * Returns the count of projections actually applied (excluding
 * already-set fields and skipped failures).
 */
export function applyProjectionsToEngine(
  engine: {
    getAnswer: (fieldId: FieldId) => unknown;
    setAnswer: (fieldId: FieldId, value: AnswerValue) => void;
  },
  projections: ReadonlyArray<{ fieldId: FieldId; value: AnswerValue }>,
  onError: (
    err: unknown,
    context: { fieldId: FieldId; valueType: AnswerValue['type'] },
  ) => void,
): number {
  let applied = 0;
  for (const { fieldId, value } of projections) {
    try {
      const existing = engine.getAnswer(fieldId);
      if (existing !== undefined) continue;
      engine.setAnswer(fieldId, value);
      applied += 1;
    } catch (err) {
      onError(err, { fieldId, valueType: value.type });
    }
  }
  return applied;
}

export function projectScreenerAnswersToCatalog(
  answers: IScreenerAnswers,
): ReadonlyArray<{ fieldId: FieldId; value: AnswerValue }> {
  const out: { fieldId: FieldId; value: AnswerValue }[] = [];

  // Bar-question consolidation. The legacy
  // individual boolean screener questions
  // (removal_final_order, removal_reinstated, criminal_drug_trafficking,
  // criminal_human_trafficking, security_terrorist_membership,
  // removal_in_proceedings, criminal_arrested, criminal_convicted,
  // public_charge_subject, security_committed_terrorist_activity,
  // unlawful_presence_over_one_year, immigration_misrepresentation)
  // collapsed into two multi-select bundles. The handoff fans the
  // bundle values back into the same catalog booleans so the intake
  // sees an identical answer shape — items in the array project to
  // `true`; items NOT in the array (when the user submitted the
  // bundle) project to `false`. The build-time test in
  // `screenerHandoff.test.ts` asserts every fieldId here exists.

  type BundleFan = {
    bundle: 'immigration_history_bundle' | 'criminal_public_charge_bundle';
    value: string;
    fieldId: FieldId;
  };
  const bundleFans: readonly BundleFan[] = [
    // Immigration-history bundle
    {
      bundle: 'immigration_history_bundle',
      value: 'final_removal_order',
      fieldId: 'eligibility.removal.has_final_order' as FieldId,
    },
    {
      bundle: 'immigration_history_bundle',
      value: 'reinstated_removal_order',
      fieldId: 'eligibility.removal.had_prior_order_reinstated' as FieldId,
    },
    {
      bundle: 'immigration_history_bundle',
      value: 'removal_proceedings',
      fieldId: 'eligibility.removal.in_or_was_in_proceedings' as FieldId,
    },
    // Criminal & public-charge bundle
    {
      bundle: 'criminal_public_charge_bundle',
      value: 'drug_trafficking',
      fieldId:
        'eligibility.criminal.trafficked_controlled_substance' as FieldId,
    },
    {
      bundle: 'criminal_public_charge_bundle',
      value: 'human_trafficking',
      fieldId:
        'eligibility.criminal.trafficked_persons_for_servitude' as FieldId,
    },
    {
      bundle: 'criminal_public_charge_bundle',
      value: 'terrorist_group_member',
      fieldId: 'eligibility.security.member_of_terrorist_group' as FieldId,
    },
    {
      bundle: 'criminal_public_charge_bundle',
      value: 'arrested_or_detained',
      fieldId:
        'eligibility.criminal.has_been_arrested_or_detained' as FieldId,
    },
    {
      bundle: 'criminal_public_charge_bundle',
      value: 'convicted',
      fieldId:
        'eligibility.criminal.has_pled_guilty_or_convicted' as FieldId,
    },
    {
      bundle: 'criminal_public_charge_bundle',
      value: 'public_charge',
      fieldId:
        'eligibility.public_charge.is_subject_to_inadmissibility_ground' as FieldId,
    },
  ];

  for (const fan of bundleFans) {
    const bundle = answers[fan.bundle];
    if (!Array.isArray(bundle) || bundle.length === 0) continue;
    out.push({
      fieldId: fan.fieldId,
      value: { type: 'boolean', value: bundle.includes(fan.value) },
    });
  }

  // Severe-harm bundle fan-out.
  const harm = answers.security_severe_harm_bundle;
  if (Array.isArray(harm) && harm.length > 0) {
    const has = (v: string): boolean => harm.includes(v);
    const harmFans: ReadonlyArray<{ value: string; fieldId: FieldId }> = [
      {
        value: 'killed',
        fieldId: 'eligibility.security.killed_any_person' as FieldId,
      },
      {
        value: 'severely_injured',
        fieldId:
          'eligibility.security.severely_injured_any_person' as FieldId,
      },
      {
        value: 'torture_or_genocide',
        fieldId:
          'eligibility.security.committed_torture_or_genocide' as FieldId,
      },
      {
        value: 'terrorist_activity',
        fieldId:
          'eligibility.security.committed_terrorist_activity' as FieldId,
      },
    ];
    for (const fan of harmFans) {
      out.push({
        fieldId: fan.fieldId,
        value: { type: 'boolean', value: has(fan.value) },
      });
    }
  }

  // J-1 follow-up — only when the parent was yes, otherwise the
  // question is "not applicable" per the screener's conditional-
  // visibility rule.
  if (
    answers.j1_two_year_rule === true &&
    typeof answers.j1_two_year_complied === 'string'
  ) {
    const complied = answers.j1_two_year_complied;
    // Catalog field is `boolean`; project completed/waiver to true,
    // 'no' to false, 'na' is dropped.
    if (complied === 'completed' || complied === 'waiver') {
      out.push({
        fieldId:
          'eligibility.j_exchange_visitor.complied_with_two_year_rule' as FieldId,
        value: { type: 'boolean', value: true },
      });
    } else if (complied === 'no') {
      out.push({
        fieldId:
          'eligibility.j_exchange_visitor.complied_with_two_year_rule' as FieldId,
        value: { type: 'boolean', value: false },
      });
    }
  }

  // Beneficiary current immigration status — select field in catalog
  // (Slice 39, 2026-05-09). The screener uses snake_case enum keys
  // for routing-logic readability; the catalog stores the USCIS-
  // friendly text the I-485 Pt1Line27 / I-765 Pt2Line24 PDF widgets
  // expect verbatim. This map translates between the two
  // representations. Keep in sync with optionsResolver's
  // IMMIGRATION_STATUS_OPTIONS keys.
  const SCREENER_TO_CATALOG_IMMIGRATION_STATUS: Readonly<Record<string, string>> = {
    lawful_entry_visa: 'Nonimmigrant visa entry',
    lawful_entry_vwp: 'Visa Waiver Program (ESTA)',
    parolee: 'Paroled into the U.S.',
    asylee_or_refugee: 'Asylee/Refugee',
    tps: 'Temporary Protected Status (TPS)',
    entered_without_inspection: 'Entered without inspection',
    other: 'Other',
  };
  if (typeof answers.beneficiary_immigration_status === 'string') {
    const catalogValue =
      SCREENER_TO_CATALOG_IMMIGRATION_STATUS[
        answers.beneficiary_immigration_status
      ];
    if (catalogValue !== undefined) {
      out.push({
        fieldId: 'beneficiary.immigration_status.current' as FieldId,
        value: { type: 'select', value: catalogValue },
      });
    }
  }

  // Sponsor citizenship — catalog field is `text` per Slice 5
  // (sponsor identity). Project to the friendly enum string.
  if (typeof answers.sponsor_citizenship === 'string') {
    const value =
      answers.sponsor_citizenship === 'us_citizen'
        ? 'us_citizen'
        : answers.sponsor_citizenship === 'lpr'
          ? 'lpr'
          : null;
    if (value !== null) {
      out.push({
        fieldId: 'sponsor.citizenship.status' as FieldId,
        value: { type: 'text', value },
      });
    }
  }

  return out;
}

// `friendlyImmigrationStatus` removed.
// The catalog field `beneficiary.immigration_status.current` is
// now a `select` and the screener writes the enum value directly.
// PDF-fill projection lives on the catalog row's
// `pdfConsumers[*].widgetValueProjection`.
